import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { AuthLayout, NotApprovedNotice } from '../../components/AuthLayout.js';
import { NOT_APPROVED, readApiError } from '../../lib/apiError.js';

export default function LoginPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [notApproved, setNotApproved] = useState(params.get('reason') === 'not_approved');

  useEffect(() => {
    if (localStorage.getItem('velinko_token')) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (res.ok) {
        const { data } = await res.json();
        localStorage.setItem('velinko_token', data.accessToken);
        localStorage.setItem('velinko_refresh_token', data.refreshToken);
        navigate('/dashboard');
      } else {
        const err = await readApiError(res, 'Sign-in failed');
        if (err.code === NOT_APPROVED) setNotApproved(true);
        else setError(err.message);
      }
    } catch (err: unknown) {
      console.error(err);
      setError('Network error');
    }
  };

  return (
    <AuthLayout title="Sign in" subtitle="Read your phone's SMS and OTP codes in the browser.">
      {error && <div className="auth-error">{error}</div>}
      {notApproved && <NotApprovedNotice />}
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className="input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="field">
          <div className="field-row">
            <label htmlFor="password">Password</label>
            <Link to="/reset-password" className="small">
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Sign in
        </button>
      </form>

      <div className="divider">or</div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <GoogleLogin
          theme="filled_black"
          onSuccess={async (credentialResponse) => {
            if (credentialResponse.credential) {
              try {
                const res = await fetch(`${import.meta.env.VITE_API_URL}/auth/google`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ idToken: credentialResponse.credential }),
                });
                if (res.ok) {
                  const { data } = await res.json();
                  localStorage.setItem('velinko_token', data.accessToken);
                  localStorage.setItem('velinko_refresh_token', data.refreshToken);
                  navigate('/dashboard');
                } else {
                  const err = await readApiError(res, 'Google sign-in failed');
                  if (err.code === NOT_APPROVED) setNotApproved(true);
                  else setError(err.message);
                }
              } catch (err: unknown) {
                console.error(err);
                setError('Network error during Google login');
              }
            }
          }}
          onError={() => {
            setError('Google Login Failed');
          }}
        />
      </div>

      <div className="auth-links">
        <Link to="/register">No account? Request access</Link>
      </div>
    </AuthLayout>
  );
}
